import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from "utils/base-query";

// DFY (Done For You) Orders API service using RTK Query
export const dfyApi = createApi({
  reducerPath: "dfyApi",
  tagTypes: ["DFY_ORDERS", "DFY_ORDER_DETAILS"],
  baseQuery: baseQuery,
  endpoints: (builder) => ({
    // Fetch orders with pagination and filtering
    getOrders: builder.query({
      query: ({ orderId, status = "", query = "", page = 1, limit = 15, csv = false, sortBy = "", sortOrder = "desc", partnerId = "", subUserId = "" }) => {
        const params = { page, limit };
        if (orderId) params.orderId = orderId;
        if (status && status !== "all" && status !== "All") params.status = status.toLowerCase();
        if (query) params.search = query;
        if (sortBy) {
          params.sortBy = sortBy;
          params.sortOrder = sortOrder;
        }
        // Partner filtering
        if (partnerId) params.partnerId = partnerId;
        // Sub-user filtering
        if (subUserId) params.subUserId = subUserId;

        // Skip pagination when exporting CSV to get all records
        if (csv) {
          params.csv = "true";
        }

        return {
          method: "GET",
          url: "/admin-whitelabel-dfy/getOrders",
          params,
        };
      },
      providesTags: ["DFY_ORDERS"],
    }),

    // Update single order status
    updateOrderStatus: builder.mutation({
      query: ({ orderId, email, contactId, status }) => ({
        method: "PUT",
        url: `/admin-whitelabel-dfy/updateOrderStatus/${orderId}`,
        body: {
          email,
          contactId,
          status: status.toLowerCase(),
        },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "DFY_ORDER_DETAILS", id: arg.orderId },
        "DFY_ORDERS",
      ],
    }),

    // Update multiple orders status in bulk
    updateBulkOrderStatus: builder.mutation({
      query: ({ orders, status }) => ({
        method: "PUT",
        url: "/admin-whitelabel-dfy/updateOrderStatus/true",
        body: {
          orders,
          status: status.toLowerCase(),
        },
      }),
      invalidatesTags: ["DFY_ORDER_DETAILS", "DFY_ORDERS"],
    }),
    updateCancelledStatus: builder.mutation({
      query: ({ orderId, cancelledFromDFY }) => ({
        url: `/admin-whitelabel-dfy/cancelled-from-dfy/${orderId}`,
        method: "PUT",
        body: { cancelledFromDFY },
      }),
      invalidatesTags: ["ORDER"],
    }),
    // Fetch domain data for a specific order (View Order details)
    getDomainData: builder.query({
      query: ({ orderId, status = "", query = "", page = 1, limit = 15 }) => {
        const params = { page, limit };
        if (status && status !== "all" && status !== "All") params.status = status.toLowerCase();
        if (query) params.search = query;

        return {
          method: "GET",
          url: `/admin-whitelabel-dfy/getDomainData/${orderId}`,
          params,
        };
      },
      providesTags: (result, error, arg) => [
        { type: "DFY_ORDER_DETAILS", id: arg.orderId },
      ],
    }),

    getPartnerAndSubuserList: builder.query({
      query: () => {
        return {
          url: `/admin-whitelabel-dfy/getPartnerAndSubuserDropdown/`,
          method: "GET"
        }
      },
      providesTags: ["PartnerAndSubusersList"],
    }),
    
    getDomainEmailInvoice: builder.query({
      query: ({ partnerId, month, year }) => {
        const params = {};
        if (partnerId) params.partnerId = partnerId;
        if (month) params.month = month;
        if (year) params.year = year;
        return {
          url: `/admin-whitelabel-dfy/getInvoices`,
          method: "GET",
          params,
        }
      },
      providesTags: ["partnerInvoice"],
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useGetOrdersQuery,
  useLazyGetOrdersQuery,
  useUpdateOrderStatusMutation,
  useUpdateBulkOrderStatusMutation,
  useUpdateCancelledStatusMutation,
  useLazyGetPartnerAndSubuserListQuery,
  useLazyGetDomainDataQuery,
  useLazyGetDomainEmailInvoiceQuery
} = dfyApi;
